import { stringify, parse } from 'qs'
import type { Routes } from 'stylewhere/pages'
import { useNavigate, useLocation, useParams, NavigateFunction, Location } from 'react-router-dom'

export type Route = { component: React.ReactNode }

interface RouteParams {
  [key: string]: any
}

interface NavigationOptions {
  replace?: boolean
  state?: any
  props?: { [key: string]: any }
}

// Router singleton per navigazione fuori dai componenti
let navigateRef: NavigateFunction | null = null
let locationRef: Location | null = null

export function setRouterRefs(navigateFn: NavigateFunction, location: Location) {
  navigateRef = navigateFn
  locationRef = location
}

export const replaceMatchParams = (page: Routes, params: Record<string, any>) => {
  Object.keys(params).forEach((key) => {
    if (page.includes(`:${key}`)) {
      page = page.replace(`:${key}`, params[key]) as Routes
      delete params[key]
    }
  })
  return page
}

export function navigate(_path: Routes, routeParams?: RouteParams, options?: NavigationOptions): void {
  // add query params to path
  const route = routeParams ? replaceMatchParams(_path as Routes, routeParams) : _path
  const stringParams = routeParams ? stringify(routeParams, { arrayFormat: 'repeat' }) : ''
  const path = `${route}${stringParams ? `?${stringParams}` : ''}`

  if (navigateRef) {
    // Usa React Router se disponibile
    navigateRef(path, { replace: options?.replace, state: options?.state })
  } else {
    // Fallback a window.location
    if (options?.replace) {
      window.history.replaceState(options?.state || {}, '', path)
    } else {
      window.history.pushState(options?.state || {}, '', path)
      window.dispatchEvent(new PopStateEvent('popstate'))
    }
  }
}

export function reloadApp(destination?: Routes) {
  if (destination) navigate(destination)
  window.location.reload()
}

export function refresh(queryParams?: RouteParams) {
  const currentPath = locationRef?.pathname || window.location.pathname
  navigate(currentPath as Routes, queryParams)
}

// Hook per ottenere i parametri della route (da usare nei componenti)
export function useMatchParams<T extends Record<string, string> = Record<string, string>>(): T {
  return useParams() as T
}

// Hook per ottenere lo state della location (da usare nei componenti)
export function useLocationState<S extends object = object>(): Partial<S> {
  const location = useLocation()
  return (location.state as Partial<S>) ?? ({} as Partial<S>)
}

// Funzioni legacy per compatibilità con class components che ricevono props
export function getMatchParams<T extends Record<string, string> = Record<string, string>>(props?: {
  match?: { params?: Record<string, string> }
}): T {
  if (props?.match?.params) {
    return props.match.params as T
  }
  // Fallback: prova a estrarre parametri da window.location.pathname
  const pathname = window.location.pathname
  const params: Record<string, string> = {}

  // Prova a estrarre opCode dal pathname se presente
  // Pattern comune: /operation/:opCode/...
  const opCodeMatch = pathname.match(/\/([^/]+)\/([^/]+)/)
  if (opCodeMatch && opCodeMatch[2]) {
    // Se il pathname ha almeno 2 segmenti, il secondo potrebbe essere opCode
    const segments = pathname.split('/').filter(Boolean)
    if (segments.length >= 2) {
      params.opCode = segments[1]
    }
  }

  // Avvisa in console se non ci sono parametri (solo in sviluppo)
  if (Object.keys(params).length === 0 && process.env.NODE_ENV === 'development') {
    console.warn('getMatchParams: No route parameters found. Component may not work correctly.', { pathname, props })
  }

  return params as T
}

export function getLocationState<S extends object = object>(props?: { location?: { state?: any } }): Partial<S> {
  if (props?.location?.state) {
    return (props.location.state as Partial<S>) ?? ({} as Partial<S>)
  }
  return {} as Partial<S>
}

export function getQueryParams(): Record<string, any> {
  const search = locationRef?.search || window.location.search
  const queryParams = search && search.charAt(0) === '?' ? search.substring(1) : search
  return parse(queryParams)
}

export function addQueryParams(newParams: Record<string, unknown>, reset = false): void {
  const search = locationRef?.search || window.location.search
  const queryParams = reset ? {} : parse(search && search.charAt(0) === '?' ? search.substring(1) : search)
  const params = {
    ...queryParams,
    ...newParams
  }
  let paramsString = ''
  Object.keys(params).map((key) => (paramsString += `${key}=${params[key]}&`))
  if (paramsString.length > 0) paramsString = paramsString.substring(0, paramsString.length - 1)
  const encoded = paramsString ? `?${encodeURI(paramsString)}` : ''
  if (search !== encoded) {
    const newPath = `${locationRef?.pathname || window.location.pathname}${encoded}`
    if (navigateRef) {
      navigateRef(newPath, { replace: false })
    } else {
      window.history.pushState({}, '', newPath)
      window.dispatchEvent(new PopStateEvent('popstate'))
    }
  }
}

export function setQueryParams(newParams: Record<string, any>) {
  addQueryParams(newParams, true)
}
